/**
 * Playing full screen. Installed as an app (Add to Home Screen, or Install) the game opens
 * without the browser's bars (public/manifest.webmanifest). In a browser tab it can go full
 * screen where the browser allows it (the Fullscreen API: Android, iPad, computers); an iPhone
 * does not, so there the game explains how to add it to the Home Screen.
 */

interface WebkitDocument {
  webkitFullscreenEnabled?: boolean;
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
}
interface WebkitElement {
  webkitRequestFullscreen?: () => Promise<void> | void;
}

const doc = (): Document & WebkitDocument => document as Document & WebkitDocument;

/**
 * Running as an installed app, without the browser's bars. A page the game itself put in full
 * screen reads as display-mode fullscreen too in recent Chrome: that is not an installed app.
 */
export function isStandalone(): boolean {
  try {
    return (
      matchMedia('(display-mode: standalone)').matches ||
      (matchMedia('(display-mode: fullscreen)').matches && !isFullscreen()) ||
      (navigator as Navigator & { standalone?: boolean }).standalone === true
    );
  } catch {
    return false;
  }
}

/** The browser can put the page in full screen, or take it out again. */
export function canGoFullscreen(): boolean {
  if (typeof document === 'undefined') return false;
  const enabled = !!(doc().fullscreenEnabled || doc().webkitFullscreenEnabled);
  return enabled && (isFullscreen() || !isStandalone());
}

export function isFullscreen(): boolean {
  return !!(doc().fullscreenElement ?? doc().webkitFullscreenElement);
}

export async function toggleFullscreen(): Promise<void> {
  try {
    if (isFullscreen()) {
      await (document.exitFullscreen?.() ?? doc().webkitExitFullscreen?.());
      return;
    }
    const root = document.documentElement as HTMLElement & WebkitElement;
    if (root.requestFullscreen) await root.requestFullscreen({ navigationUI: 'hide' });
    else await root.webkitRequestFullscreen?.();
  } catch {
    // Refused (not from a tap, or not allowed here): the page stays as it is.
  }
}

/**
 * Calls `listener` whenever the page enters or leaves full screen, or the display mode changes
 * (Chrome reports it a moment after the page enters or leaves full screen).
 */
export function onFullscreenChange(listener: () => void): () => void {
  document.addEventListener('fullscreenchange', listener);
  document.addEventListener('webkitfullscreenchange', listener);
  const modes = ['(display-mode: fullscreen)', '(display-mode: standalone)'].flatMap((q) => {
    try {
      const list = matchMedia(q);
      list.addEventListener?.('change', listener);
      return [list];
    } catch {
      return [];
    }
  });
  return () => {
    document.removeEventListener('fullscreenchange', listener);
    document.removeEventListener('webkitfullscreenchange', listener);
    for (const list of modes) list.removeEventListener?.('change', listener);
  };
}

/** An iPhone or iPad in Safari (not installed): full screen means adding it to the Home Screen. */
export function needsHomeScreen(): boolean {
  if (typeof navigator === 'undefined' || isStandalone() || canGoFullscreen()) return false;
  const ua = navigator.userAgent;
  const iPad = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  return /iPhone|iPad|iPod/.test(ua) || iPad;
}

/*
 * Installing: Chrome and Edge offer their own prompt, which the page keeps (beforeinstallprompt)
 * to show when the player asks.
 */
interface InstallPrompt extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: InstallPrompt | null = null;
const installListeners = new Set<() => void>();

export function listenForInstall(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferred = e as InstallPrompt;
    for (const l of installListeners) l();
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    for (const l of installListeners) l();
  });
}

export const canInstall = (): boolean => deferred !== null;

export async function install(): Promise<void> {
  const prompt = deferred;
  if (!prompt) return;
  deferred = null;
  await prompt.prompt();
  await prompt.userChoice.catch(() => undefined);
  for (const l of installListeners) l();
}

export function onInstallChange(listener: () => void): () => void {
  installListeners.add(listener);
  return () => installListeners.delete(listener);
}
